/**
 * PresupuestoPorPasoApiClient -- el armador paso a paso (MADERINCO-REDISENO-T1, D-126 = a; API 529 cbe384c).
 *
 * Un metodo por paso. Cada uno manda If-Match con la revision que el panel vio (salvo crear el borrador) y devuelve
 * el presupuesto releido con su nueva revision. Mientras el 529 no este servido, las rutas dan 404/405: NO_PUBLICADO.
 * Fuera del negocio demo la API las cierra (#27937): NO_HABILITADO, y el armador sigue con el guardado de siempre.
 * Nunca lanza. Los cuerpos llegan armados de application/presupuestos/porPaso.js.
 *
 * @module infrastructure/api/PresupuestoPorPasoApiClient
 */
import { ApiBase } from './ApiBase.js';
import { getSelectedBusiness, isValidBusinessId } from '../negocioElegido.js';
import { revisionDe } from '../../application/presupuestos/porPaso.js';

const BASE = '/api/v1/presupuestos';
const negocio = (sep) => { const id = getSelectedBusiness(); return isValidBusinessId(id) ? `${sep}negocio=${encodeURIComponent(id)}` : ''; };
const ruta = (...p) => `${BASE}/${p.map((x) => encodeURIComponent(x)).join('/')}${negocio('?')}`;

function codigoDe(res) {
  const d = res?.details || {};
  const tipo = String(d.type ?? d.Type ?? '').split('/').filter(Boolean).pop() || '';
  return String(d.code ?? d.codigo ?? (/^[a-z_]+$/i.test(tipo) && tipo !== 'problems' ? tipo : '')).toUpperCase();
}

function fallo(res) {
  const code = res?.code;
  const c = codigoDe(res);
  // api 573 (recorrido): un presupuesto que ya no es borrador tambien contesta 409, pero no es "otro cambio".
  if (c === 'NO_ES_BORRADOR') return { ok: false, motivo: 'NO_ES_BORRADOR', detalle: res?.details?.detail };
  if (code === 409 || c === 'VERSION_CAMBIO') return { ok: false, motivo: 'OTRO_CAMBIO' };
  if (code === 404 || code === 405) return { ok: false, motivo: c && c !== '404' && c !== 'NO_EXISTE' ? c : 'NO_PUBLICADO', detalle: res?.details?.detail };
  // 529 (712d18c): fuera del negocio demo la API contesta 403 PUERTA_CERRADA; ese y solo ese es "no habilitado" (el
  // armador vuelve al guardado de siempre). Otro 403 (NEGOCIO_AJENO, SIN_IDENTIDAD) es falta de permiso.
  if (c === 'PUERTA_CERRADA') return { ok: false, motivo: 'NO_HABILITADO', detalle: res?.details?.detail };
  if (code === 401 || code === 403) return { ok: false, motivo: 'PROHIBIDO', detalle: res?.details?.detail };
  if (code === 400) return { ok: false, motivo: 'INVALIDO', detalle: res?.details?.detail || (typeof res?.error === 'string' ? res.error : '') };
  if (code === 'NETWORK' || code === 'TIMEOUT') return { ok: false, motivo: 'SIN_RED' };
  return { ok: false, motivo: 'ERROR_API', detalle: typeof res?.error === 'string' ? res.error : res?.error?.message };
}

async function paso(fn) {
  let res;
  try { res = await fn(); } catch (e) { return { ok: false, motivo: 'SIN_RED', detalle: e?.message }; }
  if (!res || !res.ok) return fallo(res);
  const data = res.data?.data ?? res.data ?? null;
  return { ok: true, data, revision: revisionDe(data) };
}

const conRevision = (rev) => ({ headers: { 'If-Match': String(rev) } });
const sinRevision = () => ({ ok: false, motivo: 'SIN_REVISION' });

export const PresupuestoPorPasoApiClient = {
  async crearBorrador(cuerpo) {
    if (!cuerpo) return { ok: false, motivo: 'INVALIDO', detalle: 'falta el codigo del presupuesto' };
    return paso(() => ApiBase.post(`${BASE}/borrador${negocio('?')}`, cuerpo));
  },
  async agregarSeccion(id, rev, cuerpo) {
    if (rev === null || rev === undefined) return sinRevision();
    if (!cuerpo) return { ok: false, motivo: 'INVALIDO', detalle: 'la seccion necesita nombre' };
    return paso(() => ApiBase.post(ruta(id, 'secciones'), cuerpo, conRevision(rev)));
  },
  async quitarSeccion(id, rev, seccionId) {
    if (rev === null || rev === undefined) return sinRevision();
    return paso(() => ApiBase.del(ruta(id, 'secciones', seccionId), null, conRevision(rev)));
  },
  async agregarItem(id, rev, cuerpo) {
    if (rev === null || rev === undefined) return sinRevision();
    if (!cuerpo) return { ok: false, motivo: 'INVALIDO', detalle: 'el item necesita seccion, cantidad y una unidad de la lista' };
    return paso(() => ApiBase.post(ruta(id, 'items'), cuerpo, conRevision(rev)));
  },
  async cambiarItem(id, rev, itemId, cuerpo) {
    if (rev === null || rev === undefined) return sinRevision();
    if (!cuerpo) return { ok: false, motivo: 'INVALIDO', detalle: 'no hay nada que cambiar' };
    return paso(() => ApiBase.patch(ruta(id, 'items', itemId), cuerpo, conRevision(rev)));
  },
  async quitarItem(id, rev, itemId) {
    if (rev === null || rev === undefined) return sinRevision();
    return paso(() => ApiBase.del(ruta(id, 'items', itemId), null, conRevision(rev)));
  },
  async parametros(id, rev, cuerpo) {
    if (rev === null || rev === undefined) return sinRevision();
    return paso(() => ApiBase.patch(ruta(id), cuerpo || {}, conRevision(rev)));
  },
  // MADERINCO-ARBOLES-SECCIONES-T1 (contrato #30474, SI #30514): lo respondido en las secciones esenciales.
  // Mientras A no lo publique, 404/405 = NO_PUBLICADO y el recorrido sigue en la pantalla.
  async leerRecorrido(id) {
    if (!id) return { ok: false, motivo: 'INVALIDO', detalle: 'falta el presupuesto' };
    return paso(() => ApiBase.get(ruta(id, 'recorrido')));
  },
  async guardarRecorrido(id, rev, cuerpo) {
    if (!id) return { ok: false, motivo: 'INVALIDO', detalle: 'falta el presupuesto' };
    if (rev === null || rev === undefined) return sinRevision();
    return paso(() => ApiBase.put(ruta(id, 'recorrido'), cuerpo, conRevision(rev)));
  }
};
